import React, { useEffect, useState, createElement } from 'react';
import { LayoutDashboard, Heart, FileText, Video, Calculator, Globe, Shield, Check, Upload, Info, ArrowRight, Download } from 'lucide-react';
import { PlotCard } from '../components/PlotCard';
import { DashboardLayout } from '../components/DashboardLayout';
import { MessageModal } from '../components/modals/MessageModal';
import { UploadOCIPassportModal } from '../components/modals/UploadOCIPassportModal';
import { SourceOfFundsModal } from '../components/modals/SourceOfFundsModal';
interface ForexRates {
  USD: number;
  AED: number;
  lastUpdated: string;
}
export function NriBuyerDashboardPage() {
  const [timezone, setTimezone] = useState<'IST' | 'UAE'>('IST');
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [propertyValue, setPropertyValue] = useState('8500000');
  const [forexRates, setForexRates] = useState<ForexRates>({
    USD: 83.42,
    AED: 22.71,
    lastUpdated: '10 mins ago'
  });
  const [isLoadingForex, setIsLoadingForex] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isOCIModalOpen, setIsOCIModalOpen] = useState(false);
  const [isFundsModalOpen, setIsFundsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  // Calculate TDS and net repatriable amount
  const calculateTax = () => {
    const value = parseFloat(propertyValue.replace(/,/g, ''));
    const tdsRate = 0.2288;
    const tds = value * tdsRate;
    const netRepatriable = value - tds;
    return {
      tds: tds.toLocaleString('en-IN'),
      netRepatriable: netRepatriable.toLocaleString('en-IN')
    };
  };
  const taxCalc = calculateTax();
  // Fetch real-time forex rates
  useEffect(() => {
    const fetchForexRates = async () => {
      setIsLoadingForex(true);
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        if (data && data.rates && data.rates.INR) {
          const usdToInr = data.rates.INR;
          const aedToInr = usdToInr / 3.67;
          const now = new Date();
          const timeString = now.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          setForexRates({
            USD: parseFloat(usdToInr.toFixed(2)),
            AED: parseFloat(aedToInr.toFixed(2)),
            lastUpdated: `Updated at ${timeString}`
          });
        }
      } catch (error) {
        console.error('Failed to fetch forex rates:', error);
      } finally {
        setIsLoadingForex(false);
      }
    };
    fetchForexRates();
    const interval = setInterval(fetchForexRates, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  const handleDownloadReport = () => {
    const value = parseFloat(propertyValue.replace(/,/g, ''));
    const reportContent = `NRI PROPERTY TRANSACTION REPORT\nGenerated: ${new Date().toLocaleDateString()}\n\nPROPERTY DETAILS:\nProperty Value: ₹${value.toLocaleString('en-IN')}\n\nTAX CALCULATIONS:\nTDS (22.88%): ₹${taxCalc.tds}\nNet Repatriable Amount: ₹${taxCalc.netRepatriable}\n\nFOREX RATES (Live):\n1 USD = ₹${forexRates.USD}\n1 AED = ₹${forexRates.AED}\n${forexRates.lastUpdated}`;
    const blob = new Blob([reportContent], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NRI_Property_Report_${new Date().getTime()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setModalContent({
      title: 'Report Downloaded',
      message: 'Your comprehensive NRI property transaction report has been downloaded successfully.',
      type: 'success'
    });
    setIsMessageModalOpen(true);
  };
  const navItems = [{
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard/nri-buyer'
  }, {
    label: 'Shortlisted',
    icon: Heart,
    path: '/buyer/shortlisted'
  }, {
    label: 'POA & Legal',
    icon: FileText,
    path: '/nri/poa-legal'
  }, {
    label: 'Virtual Tours',
    icon: Video,
    path: '/nri/virtual-tours'
  }, {
    label: 'Tax & Forex',
    icon: Calculator,
    path: '/nri/tax-forex'
  }];
  const poaSteps = [{
    label: 'Drafting',
    status: 'completed',
    date: 'Oct 12'
  }, {
    label: 'Notarization',
    status: 'current',
    date: 'In Progress'
  }, {
    label: 'Apostille',
    status: 'pending',
    date: 'Est. Oct 20'
  }, {
    label: 'Registration',
    status: 'pending',
    date: 'Est. Oct 25'
  }];
  const handleComplianceClick = (item: string) => {
    if (item === 'OCI Card / Passport Verification') {
      setIsOCIModalOpen(true);
    } else if (item === 'Source of Funds Declaration') {
      setIsFundsModalOpen(true);
    }
  };
  return <DashboardLayout title="NRI Services" personaLabel="NRI MODE" personaColor="bg-purple-100 text-purple-800 border-purple-200" navItems={navItems} userInitials="NR" data-id="element-5056">
      <h1 className="text-2xl font-bold mb-2" data-id="element-5057">Welcome back, Neeraj</h1>
      <p className="text-gray-500 mb-8 font-mono text-sm" data-id="element-5058">
        Managing your Indian investments from Dubai, UAE.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-5059">
        {/* Virtual Tour Scheduler Widget */}
        <div className="bg-white border border-gray-300 p-6 lg:col-span-2" data-id="element-5060">
          <div className="flex justify-between items-center mb-4" data-id="element-5061">
            <h3 className="text-xs font-mono text-gray-500 uppercase flex items-center gap-2" data-id="element-5062">
              <Video className="w-3 h-3" data-id="element-5063" /> Virtual Tour Scheduler
            </h3>
            <div className="flex bg-gray-100 rounded p-1" data-id="element-5064">
              <button onClick={() => setTimezone('IST')} className={`px-3 py-1 text-xs font-bold rounded ${timezone === 'IST' ? 'bg-white shadow-sm' : 'text-gray-500'}`} data-id="element-5065">
                IST
              </button>
              <button onClick={() => setTimezone('UAE')} className={`px-3 py-1 text-xs font-bold rounded ${timezone === 'UAE' ? 'bg-white shadow-sm' : 'text-gray-500'}`} data-id="element-5066">
                UAE
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5067">
            <div className="border border-dashed border-gray-200 p-4 bg-gray-50" data-id="element-5068">
              <div className="text-sm font-bold mb-3 text-center" data-id="element-5069">
                October 2024
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2 font-mono text-gray-400" data-id="element-5070">
                <span data-id="element-5071">S</span>
                <span data-id="element-5072">M</span>
                <span data-id="element-5073">T</span>
                <span data-id="element-5074">W</span>
                <span data-id="element-5075">T</span>
                <span data-id="element-5076">F</span>
                <span data-id="element-5077">S</span>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-sm" data-id="element-5078">
                {[...Array(31)].map((_, i) => <button key={i} onClick={() => setSelectedDate(i + 1)} className={`p-1 hover:bg-purple-50 rounded ${selectedDate === i + 1 ? 'bg-purple-600 text-white font-bold' : 'text-gray-700'}`} data-id="element-5079">
                    {i + 1}
                  </button>)}
              </div>
            </div>

            <div className="space-y-4" data-id="element-5080">
              <div data-id="element-5081">
                <label className="text-xs font-mono text-gray-500 block mb-2" data-id="element-5082">
                  Available Slots ({timezone})
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-5083">
                  {['10:00 AM', '02:00 PM', '04:30 PM'].map(time => <button key={time} className="border border-gray-300 py-2 text-sm hover:border-purple-500 hover:text-purple-600 bg-white" data-id="element-5084">
                      {time}
                    </button>)}
                </div>
              </div>
              <div data-id="element-5085">
                <label className="text-xs font-mono text-gray-500 block mb-2" data-id="element-5086">
                  Platform
                </label>
                <select className="w-full border border-gray-300 p-2 text-sm bg-white" data-id="element-5087">
                  <option data-id="element-5088">Zoom Meeting</option>
                  <option data-id="element-5089">Google Meet</option>
                  <option data-id="element-5090">WhatsApp Video</option>
                </select>
              </div>
              <button className="w-full bg-[#1f2933] text-white py-2 text-sm font-bold hover:bg-gray-800" data-id="element-5091">
                Confirm Schedule
              </button>
            </div>
          </div>
        </div>

        {/* Tax Calculator Widget */}
        <div className="bg-white border border-gray-300 p-6" data-id="element-5092">
          <h3 className="text-xs font-mono text-gray-500 uppercase mb-4 flex items-center gap-2" data-id="element-5093">
            <Calculator className="w-3 h-3" data-id="element-5094" /> TDS & Tax Estimator
          </h3>
          <div className="space-y-4" data-id="element-5095">
            <div data-id="element-5096">
              <label className="text-xs font-bold block mb-1" data-id="element-5097">
                Property Value (INR)
              </label>
              <input type="text" value={propertyValue} onChange={e => setPropertyValue(e.target.value.replace(/[^0-9]/g, ''))} className="w-full border border-gray-300 p-2 font-mono text-sm" data-id="element-5098" />
            </div>

            <div className="bg-gray-50 p-3 border border-dashed border-gray-200 space-y-2" data-id="element-5099">
              <div className="flex justify-between text-sm" data-id="element-5100">
                <span className="text-gray-600" data-id="element-5101">TDS (22.88%)</span>
                <span className="font-mono font-bold" data-id="element-5102">₹ {taxCalc.tds}</span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-5103">
                <span className="text-gray-600" data-id="element-5104">Net Repatriable</span>
                <span className="font-mono font-bold text-green-700" data-id="element-5105">
                  ₹ {taxCalc.netRepatriable}
                </span>
              </div>
            </div>

            <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-100 text-xs text-yellow-800" data-id="element-5106">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-5107" />
              <p data-id="element-5108">FEMA Limit: $1M USD per financial year for repatriation.</p>
            </div>

            <button onClick={handleDownloadReport} className="w-full border border-gray-300 py-2 text-xs font-bold hover:bg-gray-50 flex items-center justify-center gap-2" data-id="element-5109">
              <Download className="w-4 h-4" data-id="element-5110" />
              Download Detailed Report
            </button>
          </div>
        </div>
      </div>

      {/* Forex Rates Widget */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-5111">
        <div className="bg-white border border-gray-300 p-6" data-id="element-5112">
          <h3 className="text-xs font-mono text-gray-500 uppercase mb-4 flex items-center gap-2" data-id="element-5113">
            <Globe className="w-3 h-3" data-id="element-5114" /> Forex Rates (Live)
          </h3>
          <div className="flex justify-between items-end mb-2" data-id="element-5115">
            <div data-id="element-5116">
              <div className="text-sm text-gray-500" data-id="element-5117">1 USD</div>
              <div className="font-bold text-xl" data-id="element-5118">
                {isLoadingForex ? '...' : `₹ ${forexRates.USD}`}
              </div>
            </div>
            <div className="text-right" data-id="element-5119">
              <div className="text-sm text-gray-500" data-id="element-5120">1 AED</div>
              <div className="font-bold text-xl" data-id="element-5121">
                {isLoadingForex ? '...' : `₹ ${forexRates.AED}`}
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400 font-mono mt-2 pt-2 border-t border-dashed border-gray-200" data-id="element-5122">
            {forexRates.lastUpdated}
          </div>
        </div>
      </div>

      {/* POA Tracker & FEMA Compliance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-5123">
        {/* POA Tracker */}
        <div className="bg-white border border-gray-300 p-6" data-id="element-5124">
          <h3 className="text-xs font-mono text-gray-500 uppercase mb-6 flex items-center gap-2" data-id="element-5125">
            <FileText className="w-3 h-3" data-id="element-5126" /> POA Progress Tracker
          </h3>
          <div className="relative" data-id="element-5127">
            <div className="absolute left-3.5 top-2 bottom-2 w-0.5 bg-gray-100" data-id="element-5128"></div>
            <div className="space-y-6 relative" data-id="element-5129">
              {poaSteps.map((step, i) => <div key={i} className="flex items-start gap-4" data-id="element-5130">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold z-10 ${step.status === 'completed' ? 'bg-green-100 text-green-700 border border-green-200' : step.status === 'current' ? 'bg-blue-100 text-blue-700 border border-blue-200 ring-2 ring-blue-50' : 'bg-gray-100 text-gray-400 border border-gray-200'}`} data-id="element-5131">
                    {step.status === 'completed' ? <Check className="w-3 h-3" data-id="element-5132" /> : i + 1}
                  </div>
                  <div className="flex-1" data-id="element-5133">
                    <div className="flex justify-between items-start" data-id="element-5134">
                      <div data-id="element-5135">
                        <div className={`text-sm font-bold ${step.status === 'pending' ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-5136">
                          {step.label}
                        </div>
                        <div className="text-xs font-mono text-gray-500" data-id="element-5137">
                          {step.date}
                        </div>
                      </div>
                      {step.status === 'current' && <button className="flex items-center gap-1 text-xs bg-white border border-gray-300 px-2 py-1 hover:bg-gray-50" data-id="element-5138">
                          <Upload className="w-3 h-3" data-id="element-5139" /> Upload
                        </button>}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* FEMA Compliance Checklist */}
        <div className="bg-purple-50 border border-purple-100 p-6" data-id="element-5140">
          <h3 className="font-bold text-purple-900 mb-4 flex items-center gap-2" data-id="element-5141">
            <Shield className="w-4 h-4" data-id="element-5142" /> FEMA Compliance Checklist
          </h3>
          <div className="space-y-3" data-id="element-5143">
            {[{
            label: 'NRO Account Linked',
            status: 'done',
            action: null
          }, {
            label: 'PAN Card Updated (NRI Status)',
            status: 'done',
            action: null
          }, {
            label: 'OCI Card / Passport Verification',
            status: 'pending',
            action: 'oci'
          }, {
            label: 'Source of Funds Declaration',
            status: 'pending',
            action: 'funds'
          }].map((item, i) => <button key={i} onClick={() => item.action && handleComplianceClick(item.label)} className={`flex items-center gap-3 bg-white p-3 border border-purple-100 w-full text-left ${item.action ? 'hover:bg-purple-50 cursor-pointer' : ''}`} data-id="element-5144">
                <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${item.status === 'done' ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'}`} data-id="element-5145">
                  {item.status === 'done' && <Check className="w-3 h-3" data-id="element-5146" />}
                </div>
                <span className={`text-sm ${item.status === 'done' ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-5147">
                  {item.label}
                </span>
                {item.status === 'pending' && <ArrowRight className="w-4 h-4 text-gray-300 ml-auto" data-id="element-5148" />}
              </button>)}
          </div>
          <div className="mt-4 pt-4 border-t border-purple-200 text-xs text-purple-700 font-mono" data-id="element-5149">
            Completion: 50% • Action Required
          </div>
        </div>
      </div>

      {/* Shortlisted Plots */}
      <div className="mb-12" data-id="element-5150">
        <div className="flex justify-between items-center mb-6" data-id="element-5151">
          <h2 className="text-xl font-bold" data-id="element-5152">NRI-Verified Plots</h2>
          <button className="text-sm font-bold text-[#1f2933] hover:underline" data-id="element-5153">
            View All
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5154">
          <PlotCard id="1" title="Premium Corner Plot in Hinjewadi Phase 1" location="Pune, Maharashtra" size="2,400 Sq. Ft" price="₹ 45 Lakhs" trustLevel="verified" data-id="element-5155" />
          <PlotCard id="5" title="Lake View Plot - Whitefield Extension" location="Bangalore, Karnataka" size="1,500 Sq. Ft" price="₹ 65 Lakhs" trustLevel="verified" data-id="element-5156" />
        </div>
      </div>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-5157" />

      <UploadOCIPassportModal isOpen={isOCIModalOpen} onClose={() => setIsOCIModalOpen(false)} data-id="element-5158" />

      <SourceOfFundsModal isOpen={isFundsModalOpen} onClose={() => setIsFundsModalOpen(false)} data-id="element-5159" />
    </DashboardLayout>;
}